// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Outsize — the bundled country dataset (Natural Earth, public domain) plus the
// three stats the whole game compares: population, area and density. Loaded at
// build time, fully offline. Facts are not copyrightable; NE vectors are PD.

import raw from './data/countries.json';

export type Pool = 'core' | 'extra' | 'territory';
export type LonLat = readonly [number, number];

export interface Country {
  id: string;
  name: string;
  cont: string;
  /** Centroid / label point [lon, lat]. */
  c: LonLat;
  pool: Pool;
  /** Population — Natural Earth POP_EST (~2019). */
  pop: number;
  /** Area in km² — computed from the public-domain outline (see gen-geodata). */
  area: number;
  /** Simplified exterior rings, each a flat [lon,lat,lon,lat,…] array. */
  rings: number[][];
}

interface RawData {
  source: string;
  sourceUrl: string;
  popNote: string;
  areaNote: string;
  countries: Country[];
}

const DATA = raw as unknown as RawData;

export const SOURCE = DATA.source;
export const SOURCE_URL = DATA.sourceUrl;
export const POP_NOTE = DATA.popNote;
export const AREA_NOTE = DATA.areaNote;

export const ALL: readonly Country[] = DATA.countries;
export const BY_ID: ReadonlyMap<string, Country> = new Map(ALL.map((c) => [c.id, c]));

export function country(id: string): Country | undefined {
  return BY_ID.get(id);
}

/** The countries eligible to appear as a card: recognisable sovereign states. */
export const PLAY_POOL: readonly Country[] = ALL.filter((c) => c.pool === 'core');

// ── stats ────────────────────────────────────────────────────────────────────

export type StatId = 'pop' | 'area' | 'density';

export interface Stat {
  id: StatId;
  /** Short label for the HUD / "which has more X?" prompt. */
  label: string;
  /** Ratio floor: the closest two values are ever allowed to be for this stat. A
   *  population figure is exact so it can be tight; area/density carry a couple of
   *  percent of measurement slack, so their floor stays clear of it. */
  floor: number;
  value(c: Country): number;
}

export const STATS: Record<StatId, Stat> = {
  pop: { id: 'pop', label: 'population', floor: 1.06, value: (c) => c.pop },
  area: { id: 'area', label: 'area', floor: 1.15, value: (c) => c.area },
  density: {
    id: 'density',
    label: 'density',
    floor: 1.15,
    value: (c) => (c.area > 0 ? c.pop / c.area : 0),
  },
};

export function statValue(c: Country, stat: StatId): number {
  return STATS[stat].value(c);
}
